import React, { useEffect, useMemo } from 'react';
import { X } from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { sumSessionParticipants } from '../dataService';
import {
  filterSessionsByDeliverer,
  filterSessionsByOrganisation,
  buildDailySeries,
  maybeBucketByWeek,
  countByField,
  sessionListStats,
} from '../utils/involvementDrilldown';

const C = ['#0B3D91', '#E31837', '#2196F3', '#F59E0B', '#10B981', '#8B5CF6', '#EC4899', '#4FC3F7'];

/**
 * Modal: timeline + breakdown for one development officer or one organisation (Data Capture sessions only).
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {'officer'|'organisation'|null} props.variant
 * @param {object} [props.officer] — row from kwata_final_development_officers
 * @param {object} [props.organisation] — { name, region?, sessions, participants }
 * @param {object[]} props.sessions — data._sessions
 */
export default function InvolvementDrillDownModal({ open, onClose, variant, officer, organisation, sessions }) {
  const filtered = useMemo(() => {
    if (!open || !variant) return [];
    if (variant === 'officer' && officer?.name) {
      return filterSessionsByDeliverer(sessions, officer.name);
    }
    if (variant === 'organisation' && organisation?.name) {
      return filterSessionsByOrganisation(sessions, organisation);
    }
    return [];
  }, [open, variant, officer, organisation, sessions]);

  const stats = useMemo(() => sessionListStats(filtered), [filtered]);

  const { chartSeries, bucket } = useMemo(() => {
    const daily = buildDailySeries(filtered);
    const { series, bucket: b } = maybeBucketByWeek(daily);
    return { chartSeries: series, bucket: b };
  }, [filtered]);

  const activityPie = useMemo(() => countByField(filtered, 'activity_type').slice(0, 12), [filtered]);
  const venuePie = useMemo(() => countByField(filtered, 'venue_type').slice(0, 12), [filtered]);

  const title = variant === 'officer' ? officer?.name : organisation?.name;
  const subtitle =
    variant === 'organisation' && organisation?.region
      ? `${organisation.region} · session reports involving this organisation`
      : variant === 'officer'
        ? 'Session reports where this name appears in DelivererName'
        : '';

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const totalP = filtered.reduce((s, r) => s + sumSessionParticipants(r), 0);

  return (
    <div
      className="drill-modal-backdrop"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="drill-modal-panel glass-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="drill-modal-title"
      >
        <div className="drill-modal-header">
          <div>
            <h2 id="drill-modal-title" className="drill-modal-title">
              {title || 'Details'}
            </h2>
            <p className="drill-modal-sub">{subtitle}</p>
          </div>
          <button type="button" className="drill-modal-close" onClick={onClose} aria-label="Close">
            <X size={22} />
          </button>
        </div>

        <div className="drill-modal-stats">
          <div className="drill-stat">
            <span className="drill-stat-val">{filtered.length}</span>
            <span className="drill-stat-lbl">Session reports</span>
          </div>
          <div className="drill-stat">
            <span className="drill-stat-val">{totalP.toLocaleString()}</span>
            <span className="drill-stat-lbl">Participants (sum)</span>
          </div>
          <div className="drill-stat">
            <span className="drill-stat-val">{stats.totalDeliveryInstances.toLocaleString()}</span>
            <span className="drill-stat-lbl">Delivery instances (Σ NumSessions)</span>
          </div>
          <div className="drill-stat">
            <span className="drill-stat-val">{stats.distinctDaysWithReports}</span>
            <span className="drill-stat-lbl">Days with ≥1 report</span>
          </div>
          <div className="drill-stat">
            <span className="drill-stat-val">{stats.maxReportsOneDay}</span>
            <span className="drill-stat-lbl">Max reports in one day</span>
          </div>
          <div className="drill-stat">
            <span className="drill-stat-val" style={{ fontSize: '0.85rem' }}>
              {stats.firstDay || '—'} → {stats.lastDay || '—'}
            </span>
            <span className="drill-stat-lbl">Start date range (from StartDate)</span>
          </div>
        </div>

        {stats.rowsMissingDate > 0 && (
          <p className="drill-modal-note">
            {stats.rowsMissingDate} report(s) have no parseable StartDate — excluded from timeline charts.
          </p>
        )}

        <div className="drill-modal-charts">
          <div className="drill-chart-block">
            <h3>Activity over time ({bucket === 'week' ? 'by week (Monday)' : 'by day'})</h3>
            <p className="drill-chart-hint">Reports per {bucket === 'week' ? 'week' : 'day'}; line = participants.</p>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={chartSeries} margin={{ bottom: 8, left: 4, right: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                <XAxis dataKey="day" tick={{ fontSize: 9 }} angle={-35} textAnchor="end" height={70} />
                <YAxis yAxisId="r" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis yAxisId="p" orientation="right" tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ borderRadius: 10, fontSize: 12 }} />
                <Legend />
                <Bar yAxisId="r" dataKey="reports" name="Reports" fill="#0B3D91" radius={[4, 4, 0, 0]} />
                <Bar yAxisId="r" dataKey="deliveryInstances" name="Delivery Σ" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="drill-chart-block">
            <h3>Participants over time</h3>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={chartSeries} margin={{ left: 4, right: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                <XAxis dataKey="day" tick={{ fontSize: 9 }} angle={-35} textAnchor="end" height={70} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ borderRadius: 10, fontSize: 12 }} />
                <Line type="monotone" dataKey="participants" name="Participants" stroke="#E31837" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="drill-pies">
            <div className="drill-chart-block drill-pie">
              <h3>Activity types</h3>
              {activityPie.length === 0 ? (
                <p className="drill-empty">No activity_type data</p>
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie
                      data={activityPie}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={80}
                      paddingAngle={2}
                      label={({ name, percent }) => `${String(name).slice(0, 14)} ${(percent * 100).toFixed(0)}%`}
                    >
                      {activityPie.map((_, i) => (
                        <Cell key={i} fill={C[i % C.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => v.toLocaleString()} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
            <div className="drill-chart-block drill-pie">
              <h3>Venue types</h3>
              {venuePie.length === 0 ? (
                <p className="drill-empty">No venue_type data</p>
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie
                      data={venuePie}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={80}
                      paddingAngle={2}
                      label={({ name, percent }) => `${String(name).slice(0, 14)} ${(percent * 100).toFixed(0)}%`}
                    >
                      {venuePie.map((_, i) => (
                        <Cell key={i} fill={C[i % C.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => v.toLocaleString()} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
